<template>
  <v-container fluid>
    <v-row justify="start">
      <v-col>
        <h1>Waterfall Test</h1>
        <p>Fill out the form sections below to generate a new test lead.</p>
      </v-col>
      <v-col
        v-if="!dataLoading && !showError"
        class="d-flex align-end justify-end"
      >
        <v-btn
          id="submit_lead_btn"
          color="primary"
          class="submit-btn mb-4"
          :loading="formLoading"
          @click="submitLead"
        >
          Submit Lead
        </v-btn>
      </v-col>
    </v-row>

    <template
      v-if="dataLoading"
    >
      <p>Loading data</p>
      <v-progress-circular
        :size="50"
        color="primary"
        indeterminate
      />
    </template>

    <v-row
      v-else
    >
      <v-col
        v-if="!showError"
      >
        <v-stepper
          v-model="step"
          non-linear
        >
          <v-stepper-header>
            <v-stepper-item
              editable
              :value="1"
              title="Lead Data"
            />

            <v-divider />

            <v-stepper-item
              editable
              :value="2"
              title="Clarity Data"
            />

            <v-divider />

            <v-stepper-item
              editable
              :value="3"
              title="Validifi Data"
            />

            <v-divider />

            <v-stepper-item
              editable
              :value="4"
              title="White Pages Data"
            />

            <v-divider />

            <v-stepper-item
              editable
              :value="5"
              title="Sentilink Data"
            />
          </v-stepper-header>

          <v-stepper-window>
            <v-stepper-window-item
              :value="1"
            >
              <WaterfallTestLeadDataForm
                v-model:lead-data="leadData"
                :campaigns="campaigns"
                :lead-providers="leadProviders"
                :us-states="usStates"
                :customers="customers"
                @changed-frequency="handleChangedFrequency"
                @changed-next-pay-date="handleChangedNextPayDate"
              />
            </v-stepper-window-item>

            <v-stepper-window-item
              :value="2"
            >
              <WaterfallTestClarityDataForm
                :clarity-data="leadData.clarityResponse"
              />
            </v-stepper-window-item>

            <v-stepper-window-item
              :value="3"
            >
              <WaterfallTestValidifiDataForm
                :lead-data="leadData"
                :campaigns="campaigns"
                :validifi-data="leadData.validifiResponse"
              />
            </v-stepper-window-item>

            <v-stepper-window-item
              :value="4"
            >
              <WaterfallTestWhitePagesFormData
                :white-pages-data="leadData.whitepagesResponse"
              />
            </v-stepper-window-item>
            <v-stepper-window-item
              :value="5"
            >
              <WaterfallTestSentilinkFormData
                :sentilink-data="leadData.sentilinkResponse"
              />
            </v-stepper-window-item>
          </v-stepper-window>
        </v-stepper>
      </v-col>

      <v-col
        v-if="showError"
        cols="12"
      >
        <v-alert
          type="error"
          dark
          border="start"
        >
          Failed to retrieve data needed to create lead.
          <ul>
            <li
              v-for="(msg, index) in errorMessages"
              :key="index"
            >
              {{ msg }}
            </li>
          </ul>
        </v-alert>
      </v-col>

      <v-dialog
        v-model="showModal"
      >
        <v-card
          flat
          class="pa-2"
        >
          <v-card-title>Result</v-card-title>
          <v-card-text>
            <v-alert
              v-show="formError"
              type="error"
              dark
              border="start"
            >
              {{ formError }}
            </v-alert>

            <vue-json-pretty
              :data="apiResponse"
              :show-line="false"
              :show-double-quotes="false"
              virtual
            />
          </v-card-text>
        </v-card>
      </v-dialog>
    </v-row>
  </v-container>
</template>

<script>
import VueJsonPretty from 'vue-json-pretty';
import 'vue-json-pretty/lib/styles.css';

import { mapActions, mapState } from 'vuex';
import WaterfallTestLeadDataForm from '@/views/Waterfall/WaterfallTestLeadDataForm.vue';
import WaterfallTestClarityDataForm from '@/views/Waterfall/WaterfallTestClarityDataForm.vue';
import WaterfallTestValidifiDataForm from '@/views/Waterfall/WaterfallTestValidifiDataForm.vue';
import WaterfallTestWhitePagesFormData from '@/views/Waterfall/WaterfallTestWhitePagesFormData.vue';
import WaterfallTestSentilinkFormData from '@/views/Waterfall/WaterfallTestSentilinkFormData.vue';

import stateList from '@/data/states.json';

import qaData from './qaData';

export default {
  components: {
    WaterfallTestLeadDataForm,
    WaterfallTestClarityDataForm,
    WaterfallTestValidifiDataForm,
    WaterfallTestWhitePagesFormData,
    WaterfallTestSentilinkFormData,
    VueJsonPretty,
  },
  data() {
    return {
      dataLoading: true,
      showError: false,
      errorMessages: [],
      customers: [],
      campaigns: [],
      leadProviders: [],
      usStates: [],
      lastpaydate: '',
      nextpaydate: '',
      secondpaydate: '',
      leadData: {},
      step: 1,
      formLoading: false,
      showModal: false,
      apiResponse: {},
      formError: '',
    };
  },
  computed: {
    ...mapState('settings', ['settings']),
  },
  async created() {
    this.leadData = { ...qaData };

    this.usStates = (stateList || []).map((state) => state.id);

    try {
      const res = await this.$axios.get('/api/v1/waterfall/test');
      const { data } = res;

      this.customers = (data?.customers || []).map((customer) => ({
        value: customer.guid,
        text: `${customer.firstname} ${customer.lastname}`,
        email: customer.email,
        phone: customer.homephone,
      }));

      this.campaigns = (data?.campaigns || []).map((campaign) => {
        const lp = data?.lead_providers?.find((v) => v.id === campaign.lead_provider_id);
        return {
          value: campaign.id,
          text: `${campaign.id} (${campaign.campaign_name} - ${campaign.product_type})`,
          apiKey: lp?.api_key,
          leadCategory: lp?.category || '',
        };
      });

      if (this.campaigns.length === 0) {
        this.errorMessages.push('No Campaigns found.');
      } else {
        this.leadData.campaign = this.campaigns[0].value;
      }

      this.leadProviders = (data?.lead_providers || []).map((provider) => ({
        value: provider.api_key,
        text: `${provider.company_name} (${provider.category})`,
      }));

      if (this.leadProviders.length === 0) {
        this.errorMessages.push('No Lead Providers found.');
      } else {
        this.leadData.api_key = this.leadProviders[0].value;
      }

      if (this.errorMessages.length > 0) {
        this.showError = true;
      }

      this.dataLoading = false;

      this.leadData.lastpaydate = data.last_pay_date;
      this.leadData.nextpaydate = data.next_pay_date;
      this.leadData.secondpaydate = data.second_pay_date;

      this.leadData.address = data.address;
      this.leadData.homephone = data.homephone;
      this.leadData.ssn = data.ssn;
      this.leadData.accountnumber = data.accountnumber;
    } catch (error) {
      this.errorMessages.push(error.message);
    }
  },
  methods: {

    getDaysByFrequency(frequency) {
      const daysByFrequency = [
        { frequency: 'W', days: 7 },
        { frequency: 'B', days: 14 },
        { frequency: 'S', days: 15 },
        { frequency: 'M', days: 30 },
      ];

      return !frequency ? 7 : daysByFrequency.find((freq) => freq.frequency === frequency).days;
    },

    calculateDatesForSemimonthly(nextDate) {
      let adjustedNextDate; let secondPayDate; let
        lastPayDate;

      if (nextDate.getDate() < 15) {
        adjustedNextDate = new Date(nextDate.getFullYear(), nextDate.getMonth(), 15);
        secondPayDate = new Date(
          adjustedNextDate.getFullYear(),
          adjustedNextDate.getMonth() + 1,
          1,
        );
        lastPayDate = new Date(
          adjustedNextDate.getFullYear(),
          adjustedNextDate.getMonth(),
          1,
        );
      } else {
        adjustedNextDate = new Date(nextDate.getFullYear(), nextDate.getMonth() + 1, 1);
        secondPayDate = new Date(adjustedNextDate.getFullYear(), adjustedNextDate.getMonth(), 15);
        lastPayDate = new Date(adjustedNextDate.getFullYear(), adjustedNextDate.getMonth() - 1, 15);
      }

      this.leadData.nextpaydate = this.formatDate(adjustedNextDate);
      this.leadData.secondpaydate = this.formatDate(secondPayDate);
      this.leadData.lastpaydate = this.formatDate(lastPayDate);
    },

    calculateDatesForOtherFrequencies(frequency, nextDate) {
      const daysNeeded = this.getDaysByFrequency(frequency);
      const adjustedNextDate = nextDate;
      const secondPayDate = this.addDays(adjustedNextDate, daysNeeded);
      const lastPayDate = this.addDays(adjustedNextDate, daysNeeded * -1);

      this.leadData.secondpaydate = this.formatDate(secondPayDate);
      this.leadData.lastpaydate = this.formatDate(lastPayDate);
    },

    calculateDatesForMonthly(nextDate) {
      const adjustedNextDate = nextDate;
      const secondPayDate = new Date(
        adjustedNextDate.getFullYear(),
        adjustedNextDate.getMonth() + 1,
        adjustedNextDate.getDate(),
      );
      const lastPayDate = new Date(
        adjustedNextDate.getFullYear(),
        adjustedNextDate.getMonth() - 1,
        adjustedNextDate.getDate(),
      );

      this.leadData.secondpaydate = this.formatDate(secondPayDate);
      this.leadData.lastpaydate = this.formatDate(lastPayDate);
    },

    updateDates(nextPayDate, frequency) {
      const nextDate = new Date(nextPayDate);

      switch (frequency) {
        case 'S':
          this.calculateDatesForSemimonthly(nextDate);
          break;
        case 'M':
          this.calculateDatesForMonthly(nextDate);
          break;
        default:
          this.calculateDatesForOtherFrequencies(frequency, nextDate);
      }
    },
    handleChangedNextPayDate(payData) {
      this.leadData.nextpaydate = payData.nextPayDate;

      this.updateDates(this.leadData.nextpaydate, payData.payFrequency);
    },

    handleChangedFrequency(payFrequency) {
      this.updateDates(this.leadData.nextpaydate, payFrequency);
    },

    addDays(dateStr, days) {
      const date = new Date(dateStr);
      date.setDate(date.getDate() + days);

      return date;
    },

    formatDate(date) {
      const yyyy = date.getFullYear();
      const mm = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero based
      const dd = String(date.getDate()).padStart(2, '0');

      return `${yyyy}-${mm}-${dd}`;
    },

    submitLead() {
      this.formLoading = true;
      this.formError = '';

      let apiUrl = 'api/';

      if (this.leadData.return_customer && this.settings.customerPortal.external_offer_enabled) {
        apiUrl = this.settings.customerPortal.return_app_url;
        this.leadData.api_key = this.settings.customerPortal.return_app_provider_api_key;
        this.leadData.campaign = this.settings.customerPortal.return_app_campaign_id;
      }

      console.log('Lead Data:', { ...this.leadData });
      // return;

      this.$axios
        .post(apiUrl, this.leadData)
        .then((res) => {
          this.apiResponse = res.data;
        })
        .catch((error) => {
          if (error.response && error.response.status === 422) {
            this.apiResponse = error.response.data;
            this.formError = 'Please fix the following errors';
          } else {
            this.setNotification({
              message: 'There was an error processing the lead, please try again',
              color: 'red',
            });
          }
        })
        .then(() => {
          this.showModal = true;
          this.formLoading = false;
        });
    },
    ...mapActions('notification', ['setNotification']),
  },
};
</script>

<style scoped>
.submit-btn {
  background-color: #205e86 !important;
  color: white !important;
  font-weight: 700 !important;
}
</style>
